import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { usePrefersReducedMotion } from "../../hooks/usePrefersReducedMotion";

const EASE = [0.16, 1, 0.3, 1];

// Closing section — cinematic "starting line" band that also carries the
// connect CTAs (LinkedIn + Founder Intake). Replaces the former Connect section.
export default function MidDivider() {
  const ref = useRef(null);
  const reduced = usePrefersReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <section
      ref={ref}
      id="connect"
      data-testid="connect-section"
      className="relative flex min-h-[92vh] items-center justify-center overflow-hidden bg-noir py-32 text-cream md:py-40"
    >
      <motion.div style={{ y: reduced ? 0 : y }} className="absolute inset-0 z-0 scale-110">
        <video
          data-testid="connect-video"
          className="h-full w-full object-cover"
          autoPlay
          loop
          muted
          playsInline
          preload="none"
          poster="/divider-poster.jpg"
        >
          <source src="/divider.mp4" type="video/mp4" />
        </video>
      </motion.div>

      {/* heavy scrim so the serif statement + CTAs punch through */}
      <div className="pointer-events-none absolute inset-0 z-[1] bg-noir/65" />
      <div className="pointer-events-none absolute inset-0 z-[1] bg-gradient-to-t from-noir via-noir/40 to-noir/70" />

      <div className="relative z-10 mx-auto w-full max-w-7xl px-6 text-left md:px-12">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: EASE }}
          className="mb-8 text-[0.72rem] font-semibold uppercase tracking-[0.32em] text-gold"
        >
          06 &mdash; Connect
        </motion.p>

        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-15%" }}
          transition={{ duration: 1, ease: EASE, delay: 0.05 }}
          className="max-w-4xl font-serif leading-[1.03] tracking-tight text-cream"
          style={{ fontSize: "clamp(2.25rem, 5.5vw, 5rem)" }}
        >
          You&rsquo;ve spent a career preparing.{" "}
          <span className="font-quote text-gold">This is the starting line.</span>
        </motion.h2>

        <motion.p
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, ease: EASE, delay: 0.15 }}
          className="mt-8 max-w-2xl text-lg leading-relaxed text-cream/80 md:text-xl"
        >
          We would love to meet you. Connect with us on LinkedIn, or through one
          of our operators.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, ease: EASE, delay: 0.25 }}
          className="mt-12 flex flex-col items-start justify-start gap-5 sm:flex-row"
        >
          <a
            data-testid="linkedin-cta"
            href="https://www.linkedin.com/company/govilventures/"
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex items-center gap-3 rounded-full bg-gold px-9 py-4 text-[0.72rem] font-semibold uppercase tracking-[0.2em] text-noir transition-transform duration-300 hover:scale-[1.03] hover:shadow-[0_0_40px_rgba(201,162,39,0.35)]"
          >
            Connect on LinkedIn
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
        </motion.div>
      </div>
    </section>
  );
}
